import { Player, DrmScheme, MediaType, PlaybackOptions } from '@quickplay/player-interface';

import { ExternalPlayer } from '../ExternalPlayer';
import { createCorePlayer } from './CorePlayer';
import { PlayerBuilder } from '../PlayerBuilder';
import { createLogger, Logger, LoggerLevel } from '@quickplay/foundation';
import { ThumbnailPreviewConfig } from '../thumbnailPreview/ThumbnailPreviewConfiguration';

const TAG_NAME = 'FL_PLAYER';

export interface WebMafPlayerBuilder extends PlayerBuilder {
  readonly drmLicenseCustomData?: string;

  licenseCustomData(customData: string): WebMafPlayerBuilder;
}

export function CoreWebMafPlayerBuilder(): WebMafPlayerBuilder {
  let contentUrl!: string;
  let mediaFormatType: MediaType = MediaType.DASH;
  let licenseUrl = '';
  let drmType: DrmScheme = DrmScheme.NONE;
  const options: PlaybackOptions = {
    autoPlayOnLoad: true,
    shouldRetryOnErrors: true,
    maxRetries: 1,
    retiresAttempted: 0,
    repeatWhenFinished: false,
    enforceHDCPCompliance: false,
    initialPlaybackTime: undefined,
    enableEngagementScore: false,
  };
  let serverCertificate: ArrayBuffer;
  let sideloadedtracks: any[];
  let defaultLogger: Logger = createLogger(LoggerLevel.OFF, TAG_NAME);
  let customData: string | undefined = undefined;
  let config: ThumbnailPreviewConfig;
  let thumbnailPreviewEndpointUrl: string | undefined;
  let webVTTThumbnailPreviewEndpointUrl: string | undefined;
  let library: any;

  return {
    get contentUrl(): string {
      return contentUrl;
    },

    get extPlayer(): any {
      return ExternalPlayer.WebMAF;
    },

    get mediaFormatType(): MediaType {
      return mediaFormatType || MediaType.DASH;
    },

    get licenseUrl(): string {
      return licenseUrl || '';
    },

    get drmType(): DrmScheme {
      return drmType || DrmScheme.PLAYREADY;
    },

    get options(): PlaybackOptions {
      return options;
    },

    get configuredLogger(): Logger {
      return defaultLogger;
    },

    get serverCertificate(): ArrayBuffer {
      return serverCertificate;
    },

    get getSideLoadedTracks(): any[] {
      return sideloadedtracks;
    },

    get drmLicenseCustomData(): string | undefined {
      return customData;
    },

    get config(): ThumbnailPreviewConfig {
      return config;
    },

    get thumbnailPreviewEndpointUrl(): string | undefined {
      return thumbnailPreviewEndpointUrl;
    },

    get webVTTThumbnailPreviewEndpointUrl(): string | undefined {
      return webVTTThumbnailPreviewEndpointUrl;
    },
    get library(): any {
      return library;
    },

    mediaUrl(mediaUrl: string): PlayerBuilder {
      /**
       * Note: [RSM-309] - Manifest filtering was not working with PS4 Platform due to
       * `encodeURI(mediaUrl.trim())` Dual encoding of manifest uri
       */

      contentUrl = mediaUrl;
      return this;
    },

    mediaType(type: MediaType): PlayerBuilder {
      mediaFormatType = type;
      return this;
    },

    enableThumbnailPreview(thumbnailPreviewEndpointUrl_: string, config_: ThumbnailPreviewConfig = {}): PlayerBuilder {
      thumbnailPreviewEndpointUrl = thumbnailPreviewEndpointUrl_;
      config = config_;
      return this;
    },

    webVTTThumbnailPreview(webVTTTrackUrl: string): PlayerBuilder {
      webVTTThumbnailPreviewEndpointUrl = webVTTTrackUrl;
      return this;
    },

    mediaElement(_: HTMLVideoElement): PlayerBuilder {
      return this;
    },

    mediaWrapperElement(_: HTMLElement): PlayerBuilder {
      return this;
    },

    drmLicenseUrl(drmLicenseUrl: string): PlayerBuilder {
      licenseUrl = drmLicenseUrl;
      return this;
    },

    drmScheme(drmScheme: DrmScheme): PlayerBuilder {
      drmType = drmScheme;
      return this;
    },

    playbackOptions(playbackOptions: PlaybackOptions): PlayerBuilder {
      Object.assign(options, playbackOptions);
      return this;
    },

    usePlayer(_: ExternalPlayer): PlayerBuilder {
      return this;
    },

    certificate(certificate: ArrayBuffer): PlayerBuilder {
      serverCertificate = certificate;
      return this;
    },

    sideCarTracks(vttTracks: any[]): PlayerBuilder {
      sideloadedtracks = vttTracks;
      return this;
    },

    logger(logger: Logger): PlayerBuilder {
      defaultLogger = logger;
      return this;
    },

    licenseCustomData(data: string): WebMafPlayerBuilder {
      customData = data;
      return this;
    },

    playerLibrary(playerLib: any): PlayerBuilder {
      library = playerLib;
      return this;
    },

    build(): Player {
      return createCorePlayer(this);
    },
  };
}
